<template>
  <page content-class="admin-campaign-list">
    <div class="d-flex justify-content-between align-items-center">
      <h3 class="py-3 m-0"><strong>{{ $t('campaign-admin.titles.campaigns') }}</strong></h3>
      <b-button @click="createNewCampaign" variant="primary">{{ $t('campaign-admin.titles.new-campaign') }}</b-button>
    </div>
    <b-card v-if="Array.isArray(campaigns)" class="mt-2" body-class="py-0 px-3">
      <b-row
        v-for="campaign in campaigns"
        :key="campaign.id"
        :campaign="campaign"
        class="border-bottom py-2"
      >
        <b-col cols="12" class="d-flex justify-content-between align-items-center p-3">
          <div class="d-flex justify-content-start align-items-center">
            <b-img v-if="campaign.branding && campaign.branding.colour" blank :blank-color="campaign.branding.colour"
              class="mr-3" height="18px" width="18px" rounded/>
            <h4 class="m-0 font-weight-bold">
              <router-link :to="{ name: initialRouteName, params: { campaignID: campaign.id } }">{{ campaign.title }}</router-link>
            </h4>
          </div>
          <span v-if="campaign.status && statusMap[campaign.status]"
            class="status-label py-1 px-3" :style="{backgroundColor: statusMap[campaign.status].colour }">
              {{ statusMap[campaign.status].label }}
          </span>
        </b-col>
        <b-col cols="12" class="d-flex justify-content-between align-items-center pb-3 pt-2 px-3">
          <div class="d-flex justify-content-start align-items-center">
            <span>{{ $t('campaign.start-date') }}<br>
              <template v-if="campaign.startDate">{{ campaign.startDate | formatDate('Do MMM, HH:mm') }}</template>
              <span v-else class="font-italic">{{ $t('general.none') }}</span>
            </span>
            <span class="mx-3">-</span>
            <span>{{ $t('campaign.end-date') }}<br>
              <template v-if="campaign.endDate">{{ campaign.endDate | formatDate('Do MMM, HH:mm') }}</template>
              <span v-else class="font-italic">{{ $t('general.none') }}</span>
            </span>
          </div>
          <div class="d-flex justify-content-end align-items-center">
            <!-- <b-btn size="sm" @click="() => duplicateCampaign(campaign.id)" variant="link">Duplicate</b-btn> -->
            <b-btn class="ml-3 px-3" size="sm" :to="{ name: initialRouteName, params: { campaignID: campaign.id } }"
              variant="primary">{{ $t('ctas.pencil-edit') }}</b-btn>
          </div>
        </b-col>
      </b-row>
    </b-card>
  </page>
</template>

<script>
import _pick from 'lodash/pick';
import { mapRoot } from '@src/mixins/root';
import CampaignAdminListAllQuery from '@/graphql/queries/CampaignAdminListAll.gql';
import CampaignAdminCreateMutation from '@/graphql/mutations/CampaignAdminCreate.gql';
import Page from '@/components/campaign-admin/page';

export const campaignStatusMap = {
  DRAFT: {
    label: 'Draft',
    colour: '#82ccdd',
  },
  READY: {
    label: 'Ready',
    colour: '#b8e994',
  },
  IN_PROGRESS: {
    label: 'In Progress',
    colour: '#b8e994',
  },
  FINISHED: {
    label: 'Finished',
    colour: '#b8e994',
  },
  ARCHIVED: {
    label: 'Archived',
    colour: '#b8e994',
  },
};

export default {
  name: 'CampaignAdminCampaigns',
  components: {
    Page,
  },
  apollo: {
    campaigns: {
      query: CampaignAdminListAllQuery,
      update({ company }) {
        const { campaigns } = company;
        return Array.isArray(campaigns) ? campaigns : [];
      },
      error(err) {
        this.$notifyFatalErr(err, { prepend: this.$t('campaign-admin.errors.load-campaigns') });
      },
      errorPolicy: 'all',
      fetchPolicy: 'network-only',
    },
  },
  computed: {
    ...mapRoot([ 'user', 'isRecruiter' ]),
    initialRouteName: () => 'EditCampaignJobs',
    // @TODO Move this statusMap to the backend? Customisable status colours for customers?
    statusMap: () => campaignStatusMap,
  },
  methods: {
    async createNewCampaign() {
      try {
        const { data } = await this.$apollo.mutate({
          mutation: CampaignAdminCreateMutation,
          variables: { create: {} },
        });

        const { createCampaign: newCampaign } = data || {};
        this.$assert(newCampaign && newCampaign.id, 'Missing new campaign ID from mutation');
        this.$router.push({ name: this.initialRouteName, params: { campaignID: newCampaign.id }, query: { new: true } });
      } catch (err) {
        this.$notifyErr(err, { prepend: this.$t('campaign-admin.errors.create-error') });
      }
    },
    async duplicateCampaign(campaignID) {
      try {
        const campaign = this.campaigns.find(c => c && c.id === campaignID);
        this.$assert(campaign && campaign.id, this.$t('campaign-admin.errors.existing-data'));

        const create = _pick(campaign, [
          'title', 'tagline', 'startDate', 'endDate',
          'branding', 'preferences',
        ]);
        create.title = `${create.title} (Copy)`;

        const { data } = await this.$apollo.mutate({
          mutation: CampaignAdminCreateMutation,
          variables: { create },
        });

        const { createCampaign: newCampaign } = data || {};
        this.$assert(newCampaign && newCampaign.id, 'Missing new campaign ID from mutation');
        this.$router.push({ name: this.initialRouteName, params: { campaignID: newCampaign.id } });
      } catch (err) {
        this.$notifyErr(err, { prepend: 'Failed to duplicate campaign' });
      }
    }
  },
};
</script>

<style lang="scss">
.admin-campaign-list {
  .status-label {
    display: inline-block;
    border-radius: 3px;
  }
}
</style>



// WEBPACK FOOTER //
// src/views/campaign-admin/Campaigns.vue